/**
 * カタログメタ編集ダイアログ (新規 / 既存編集の両対応)。
 *
 * 入力: category, version, label, isActive, notes
 * バージョン名は「年_季節_種別」テンプレで自動生成補助 (空欄時のみ).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { useCommissionSets } from '@/hooks/useCommissions';
import {
  useUpsertProductCatalog,
  type ProductCatalogRecord,
} from '@/hooks/useProductCatalogs';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = 新規作成 / object = 既存編集 */
  existing: ProductCatalogRecord | null;
}

const SEASONS = [
  { value: 'spring', label: '春' },
  { value: 'summer', label: '夏' },
  { value: 'autumn', label: '秋' },
  { value: 'winter', label: '冬' },
  { value: 'jan', label: '1月' },
  { value: 'feb', label: '2月' },
  { value: 'mar', label: '3月' },
  { value: 'apr', label: '4月' },
  { value: 'may', label: '5月' },
  { value: 'jun', label: '6月' },
  { value: 'jul', label: '7月' },
  { value: 'aug', label: '8月' },
  { value: 'sep', label: '9月' },
  { value: 'oct', label: '10月' },
  { value: 'nov', label: '11月' },
  { value: 'dec', label: '12月' },
];

const TYPES = [
  { value: 'general', label: '通常' },
  { value: 'sale', label: 'セール' },
  { value: 'campaign', label: 'キャンペーン' },
  { value: 'special', label: '特別' },
];

function autoVersion(): string {
  const y = new Date().getFullYear();
  return `${y}_general`;
}

export function CatalogEditorDialog({ open, onOpenChange, existing }: Props) {
  const upsert = useUpsertProductCatalog();
  const { data: commissionSets } = useCommissionSets();
  const isEdit = !!existing;

  const [category, setCategory] = useState('');
  const [version, setVersion] = useState('');
  const [label, setLabel] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [notes, setNotes] = useState('');
  const [season, setSeason] = useState<'normal' | 'sale'>('normal');
  const [linkedCommissionSetId, setLinkedCommissionSetId] =
    useState<string>('');

  // テンプレ補助
  const [tplYear, setTplYear] = useState(String(new Date().getFullYear()));
  const [tplSeason, setTplSeason] = useState('general');
  const [tplType, setTplType] = useState('general');

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setCategory(existing.category);
      setVersion(existing.version);
      setLabel(existing.label || '');
      setIsActive(existing.isActive);
      setNotes(existing.notes ?? '');
      setSeason(existing.season === 'sale' ? 'sale' : 'normal');
      setLinkedCommissionSetId(existing.linkedCommissionSetId ?? '');
    } else {
      setCategory('');
      setVersion('');
      setLabel('');
      setIsActive(false);
      setNotes('');
      setSeason('normal');
      setLinkedCommissionSetId('');
    }
  }, [open, existing]);

  const fillFromTemplate = () => {
    const seasonPart = tplSeason === 'general' ? '' : `_${tplSeason}`;
    const typePart = tplType === 'general' ? '_general' : `_${tplType}`;
    setVersion(`${tplYear}${seasonPart}${typePart}`);
  };

  const handleSubmit = async () => {
    const c = category.trim();
    const v = version.trim() || autoVersion();
    if (!c) {
      toast.error('カテゴリは必須です');
      return;
    }
    try {
      await upsert.mutateAsync({
        id: existing?.id,
        category: c,
        version: v,
        label: label.trim() || undefined,
        isActive,
        notes: notes.trim() || null,
        season,
        linkedCommissionSetId: linkedCommissionSetId || null,
      });
      toast.success(isEdit ? 'カタログを更新しました' : 'カタログを作成しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'カタログ編集' : '新規カタログ作成'}</DialogTitle>
          <DialogDescription>
            メーカーブランドごとに価格表バージョンを管理します。
            {isActive
              ? '「適用中」にすると同カテゴリの他バージョンは自動的に解除されます。'
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">ブランド (メーカー) *</Label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="例: TimeWaver / Energy 4 Life"
              disabled={isEdit /* 既存カタログはブランド変更不可 */}
            />
          </div>

          <div>
            <Label className="text-xs">バージョン名 *</Label>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="例: 2025_spring_general"
            />
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              空欄なら「年_general」を自動使用 (例: {autoVersion()})
            </p>

            {!isEdit && (
              <div className="mt-2 p-2 rounded-md bg-[var(--bg-elevated)] border border-[var(--border)]">
                <div className="text-[10px] text-[var(--text-muted)] mb-1.5">
                  テンプレから生成 (任意)
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={tplYear}
                    onChange={(e) => setTplYear(e.target.value)}
                    placeholder="2025"
                    className="text-xs"
                  />
                  <select
                    value={tplSeason}
                    onChange={(e) => setTplSeason(e.target.value)}
                    className="rounded border border-[var(--border)] bg-[var(--bg-input)] text-xs px-2"
                  >
                    <option value="general">(指定なし)</option>
                    {SEASONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={tplType}
                    onChange={(e) => setTplType(e.target.value)}
                    className="rounded border border-[var(--border)] bg-[var(--bg-input)] text-xs px-2"
                  >
                    {TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={fillFromTemplate}
                  className="mt-2 text-[10px]"
                >
                  → バージョン名に反映
                </Button>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">表示ラベル (任意)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="例: 2025年春季 通常価格"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">季節モード *</Label>
              <select
                value={season}
                onChange={(e) =>
                  setSeason(e.target.value === 'sale' ? 'sale' : 'normal')
                }
                className="w-full px-2 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="normal">通常期 (normal)</option>
                <option value="sale">セール期 (sale)</option>
              </select>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                営業設定の「現在の季節」がこれに一致するときだけ effective になります。
              </p>
            </div>
            <div>
              <Label className="text-xs">紐付け 報酬セット (任意)</Label>
              <select
                value={linkedCommissionSetId}
                onChange={(e) => setLinkedCommissionSetId(e.target.value)}
                className="w-full px-2 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">(紐付けなし — 報酬計算スキップ)</option>
                {(commissionSets ?? []).map((cs) => (
                  <option key={cs.id} value={cs.id}>
                    {cs.name}
                    {cs.season ? ` [${cs.season === 'sale' ? 'セール' : '通常'}]` : ''}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                このカタログが effective なときに自動適用される報酬セット。
              </p>
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>このバージョンを適用中にする ({season === 'sale' ? 'セール期' : '通常期'})</span>
          </label>
          <p className="text-[10px] text-[var(--text-muted)] -mt-2 pl-5">
            ※ 同ブランド × 同季節で他に適用中のものがあれば自動解除されます（異季節のものは影響なし）
          </p>

          <div>
            <Label className="text-xs">備考 (任意)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="例: 為替120円換算 / 卸値ベース など"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={upsert.isPending}
          >
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={upsert.isPending || !category.trim()}
          >
            {upsert.isPending ? '保存中…' : isEdit ? '更新' : '作成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
